import { TimerEvent } from '@Core/Scene/Scene';
import { EWinPresentationStates } from '@Core/Win/States/WinPresentationStates';
import { Game } from '@Game/Game';
import { GameStore } from '@GameStore/GameStore';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { EButtonEvents } from '@Managers/Events/ButtonEvents';
import EventsCenter from '@Managers/Events/EventsCenter';
import { ALL_EVENTS_LIST, EGameEvents } from '@Managers/Events/GameEvents';
import { EGameStates } from '@Managers/StateManager/GameStates';

export type TAutoPlayStates = EGameStoreKeys.AutoPlayState | EGameStoreKeys.FreeGamesAutoPlayState;
export class AutoPlayManager {
    protected readonly game: Game;
    protected readonly gameStore: GameStore;
    protected readonly state: TAutoPlayStates;
    protected nextSpinTimer: TimerEvent;

    constructor(game: Game, state: TAutoPlayStates) {
        this.game = game;
        this.gameStore = this.game.gameStore;
        this.state = state;
        EventsCenter.on(EGameStates.IDLE_STATE, this.onIdleStart, this);
        EventsCenter.on(EGameStates.SPIN_STATE, this.onSpinStart, this);
        EventsCenter.on(EWinPresentationStates.POST_WIN_PRESENTATION_STATE, this.onCompleteWinPresentation, this);
        EventsCenter.on(ALL_EVENTS_LIST.PRE_SPECIAL_GAMES_INTRO_STATE, this.onSpecialGamesIntro, this);
        EventsCenter.on(ALL_EVENTS_LIST.SPECIAL_GAMES_SUMMARY_STATE, this.onSpecialGamesSummary, this);
    }

    public onIdleStart(): void {
        const autoPlayState = this.gameStore.get(this.state);

        if (!autoPlayState.isEnabled) return;

        if (autoPlayState.currentCount <= 0) {
            return this.stopAutoPlay();
        }

        this.nextSpinTimer = this.game.gameScene.time.delayedCall(autoPlayState.delayBeforeSpin, () => {
            EventsCenter.emit(EButtonEvents.SPIN_BUTTON_PRESSED);
        });
    }

    public onSpinStart(): void {
        this.removeScheduledSpin();
        const autoPlayState = this.gameStore.get(this.state);

        if (autoPlayState.isEnabled) {
            this.gameStore.set(this.state, { currentCount: autoPlayState.currentCount - 1 }, true);
        }
    }

    public stopAutoPlay(): void {
        EventsCenter.emit(EGameEvents.AUTOPLAY_MODE_END);
        this.gameStore.set(this.state, { isEnabled: false, currentCount: 0, totalCount: 0, isSuspended: false }, true);
        this.removeScheduledSpin();
    }

    public removeScheduledSpin(): void {
        this.nextSpinTimer?.destroy();
    }

    public onCompleteWinPresentation(): void {
        const currentGameState = this.gameStore.get(EGameStoreKeys.CurrentGameState);
        if (this.isInAnyAutoMode() && currentGameState === EGameStates.POST_WIN_STATE) EventsCenter.emit(EButtonEvents.TAKE_BUTTON_PRESSED);
    }

    private isInAnyAutoMode(): boolean {
        const isAuto = this.gameStore.get(EGameStoreKeys.AutoPlayState).isEnabled;
        const isFreeGames = this.gameStore.get(EGameStoreKeys.FreeGamesAutoPlayState).isEnabled;

        return isAuto || isFreeGames;
    }

    private onSpecialGamesIntro(): void {
        //TODO: check for nested special games
        this.gameStore.set(this.state, { isEnabled: false, isSuspended: true }, true);
    }

    private onSpecialGamesSummary(): void {
        const autoPlayState = this.gameStore.get(this.state);
        //TODO: check for nested special games
        this.gameStore.set(this.state, { isEnabled: autoPlayState.currentCount > 0, isSuspended: false }, true);
    }
}
